import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, TruckIcon, Scale, Wallet, TrendingDown, RefreshCw, Trophy, ReceiptText, Package, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "../api";
import { GrafikHarian } from "../components/GrafikHarian";

const fmt = (v, dec = 0) => (v == null ? "-" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: dec, minimumFractionDigits: 0 }));
const REFRESH_MS = 30000;

const MetricCard = ({ title, value, sub, icon: Icon, accent, testId }) => (
  <Card className="border-zinc-200 shadow-sm">
    <CardContent className="p-5 flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{title}</p>
        <p data-testid={testId} className="text-2xl font-semibold tracking-tight text-zinc-900 mt-1.5 tabular-nums">{value}</p>
        {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </CardContent>
  </Card>
);

export default function EksekutifPage() {
  const [data, setData] = useState(null);
  const [laba, setLaba] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/dashboard/eksekutif").then(({ data }) => {
      setData(data);
      setUpdatedAt(new Date());
    }).catch(() => {});
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const from = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    const to = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate())}`;
    api.get("/laporan/laba-rugi", { params: { date_from: from, date_to: to, plat: "semua" } })
      .then(({ data }) => setLaba(data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  if (!data)
    return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="eksekutif-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Dashboard Pemilik</h1>
          <p className="text-sm text-zinc-500 mt-1">Metrik operasional real-time — periode berjalan {data.periode_bulan}</p>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span data-testid="eksekutif-updated-at" className="text-xs text-zinc-500 tabular-nums">
              Diperbarui {updatedAt.toLocaleTimeString("id-ID")} • auto tiap 30 dtk
            </span>
          )}
          <Button data-testid="eksekutif-refresh-button" variant="outline" size="sm" onClick={load} className="border-zinc-300">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Muat Ulang
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {laba && (
          <div data-testid="metric-laba-bulan-wrapper" onClick={() => navigate("/laba-rugi")}
            className="cursor-pointer transition-transform duration-150 hover:-translate-y-0.5">
            <Card className={`shadow-sm ${laba.laba_bersih >= 0 ? "border-emerald-300 bg-emerald-50/60" : "border-red-300 bg-red-50/60"}`}>
              <CardContent className="p-5 flex items-start justify-between">
                <div>
                  <p className={`text-xs uppercase tracking-wider font-medium ${laba.laba_bersih >= 0 ? "text-emerald-700" : "text-red-700"}`}>Laba Bulan Berjalan</p>
                  <p data-testid="metric-laba-bulan" className={`text-2xl font-semibold tracking-tight mt-1.5 tabular-nums ${laba.laba_bersih >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                    Rp {fmt(laba.laba_bersih)}
                  </p>
                  <p className={`text-xs mt-1 ${laba.laba_bersih >= 0 ? "text-emerald-700/80" : "text-red-700/80"}`}>
                    {laba.margin_persen !== null ? `Margin ${laba.margin_persen}%` : "Belum ada pendapatan"} • klik untuk rincian
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${laba.laba_bersih >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {laba.laba_bersih >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        <MetricCard testId="metric-trip-aktif" title="Total Trip Aktif Hari Ini" value={fmt(data.trip_aktif)}
          sub={`${fmt(data.trip_berangkat)} Berangkat • ${fmt(data.trip_bongkar)} Bongkar`}
          icon={TruckIcon} accent="bg-emerald-100 text-emerald-700" />
        <MetricCard testId="metric-tonase-bulan" title="Tonase Diangkut Bulan Ini" value={`${fmt(data.tonase_bulan_ini, 2)} KG`}
          sub={`${fmt(data.trip_selesai_bulan_ini)} trip berstatus Selesai`}
          icon={Scale} accent="bg-blue-100 text-blue-700" />
        <MetricCard testId="metric-uang-jalan-bulan" title="Uang Jalan Bulan Ini" value={`Rp ${fmt(data.uang_jalan_bulan_ini)}`}
          sub="Total pengeluaran harian seluruh trip"
          icon={Wallet} accent="bg-amber-100 text-amber-700" />
        <MetricCard testId="metric-susut-over" title="Susut Melebihi Toleransi" value={`${fmt(data.susut_over_toleransi_kg, 2)} KG`}
          sub={`${fmt(data.trip_susut_over)} trip berstatus merah bulan ini`}
          icon={TrendingDown} accent="bg-red-100 text-red-700" />
        <MetricCard testId="metric-piutang" title="Piutang Berjalan" value={`Rp ${fmt(data.piutang_berjalan)}`}
          sub={`${fmt(data.invoice_belum_lunas)} invoice belum lunas`}
          icon={ReceiptText} accent="bg-sky-100 text-sky-700" />
        <MetricCard testId="metric-nilai-gudang" title="Nilai Aset Gudang" value={`Rp ${fmt(data.nilai_aset_gudang)}`}
          sub={`${fmt(data.jenis_barang_gudang)} jenis barang • ${fmt(data.total_stok_unit)} unit stok`}
          icon={Package} accent="bg-teal-100 text-teal-700" />
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold tracking-tight">Tonase Muat vs Bongkar per Minggu (8 minggu terakhir)</CardTitle>
          <p className="text-xs text-zinc-500">Selisih batang yang melebar menandakan potensi kebocoran muatan di jalan</p>
        </CardHeader>
        <CardContent className="h-80" data-testid="chart-mingguan">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={data.per_minggu} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="minggu" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("id-ID")} />
              <Tooltip formatter={(v, name) => [`${Number(v).toLocaleString("id-ID")} KG`, name]}
                labelFormatter={(l) => `Minggu mulai ${l}`} />
              <Legend />
              <Bar dataKey="muat" name="Muat (KG)" fill="#047857" radius={[3, 3, 0, 0]} />
              <Bar dataKey="bongkar" name="Bongkar (KG)" fill="#93C5FD" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" /> Peringkat Supir Paling Hemat Susut (Bulan Ini)
          </CardTitle>
          <p className="text-xs text-zinc-500">Diurutkan dari persentase susut terkecil terhadap tonase muat — apresiasi untuk supir terbaik</p>
        </CardHeader>
        <CardContent className="p-0">
          {data.peringkat_supir.length === 0 ? (
            <p data-testid="peringkat-supir-empty" className="px-5 py-10 text-center text-sm text-zinc-400">
              Belum ada trip dengan data penyusutan pada bulan berjalan
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="peringkat-supir-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["Peringkat", "Nama Supir", "Trip", "Muat (KG)", "Susut Total (KG)", "Susut Rata² (KG)", "% Susut"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.peringkat_supir.map((s, i) => {
                    const medal = ["bg-amber-100 text-amber-800 border-amber-300",
                      "bg-zinc-200 text-zinc-700 border-zinc-300",
                      "bg-orange-100 text-orange-800 border-orange-300"][i];
                    return (
                      <tr key={s.nama} data-testid={`peringkat-row-${i + 1}`}
                        className={`border-b border-zinc-100 transition-colors duration-200 ${i === 0 ? "bg-emerald-50/60 hover:bg-emerald-50" : "hover:bg-zinc-50"}`}>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center gap-1 min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-semibold border ${medal || "border-transparent text-zinc-500"}`}>
                            {i === 0 && <Trophy className="w-3 h-3" />} #{i + 1}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{s.nama}</td>
                        <td className="px-4 py-2.5 tabular-nums">{s.trips}</td>
                        <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{fmt(s.muat, 2)}</td>
                        <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap ${s.susut_total > 0 ? "text-emerald-700" : s.susut_total < 0 ? "text-red-700" : ""}`}>{fmt(s.susut_total, 2)}</td>
                        <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap ${s.susut_avg > 0 ? "text-emerald-700" : s.susut_avg < 0 ? "text-red-700" : ""}`}>{fmt(s.susut_avg, 2)}</td>
                        <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap font-semibold ${i === 0 ? "text-emerald-700" : "text-zinc-700"}`}>
                          {fmt(s.susut_pct, 3)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <GrafikHarian prefix="eks" />
    </div>
  );
}
